import { useCallback, useEffect, useMemo, useState, type PropsWithChildren } from "react";
import { useQuery } from "@apollo/client";
import { useSearchParams } from "react-router-dom";
import useCapabilities, { useCanViewCareerServicesDashboardBool } from "@/hooks/useCapabilities";
import {
  CareerServicesDashboardSectionSelectionContext,
  type CareerServicesDashboardSectionSelection
} from "../_hooks/useCareerServicesDashboardSectionSelection";
import {
  CAREER_SERVICES_DASHBOARD_SECTIONS_QUERY,
  type CareerServicesDashboardSectionsQueryResponse
} from "../queries";

const EMPTY_SECTIONS: CareerServicesDashboardSectionsQueryResponse["careerServicesDashboardSections"] = [];

export default function CareerServicesDashboardSectionProvider({ children }: PropsWithChildren) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [allSectionsSelected, setAllSectionsSelected] = useState(false);
  const preferredSectionId = searchParams.get("sectionId");
  const { loaded: capabilitiesLoaded } = useCapabilities();
  const canViewCareerServicesDashboard = useCanViewCareerServicesDashboardBool();
  const queryEnabled = capabilitiesLoaded && canViewCareerServicesDashboard;
  const { data, loading, error, refetch } = useQuery<CareerServicesDashboardSectionsQueryResponse>(
    CAREER_SERVICES_DASHBOARD_SECTIONS_QUERY,
    { fetchPolicy: "network-only", skip: !queryEnabled }
  );
  const requiresSectionSelection = queryEnabled && data?.careerServicesDashboardSectionSelectionRequired === true;
  const sections = queryEnabled && !error ? (data?.careerServicesDashboardSections ?? EMPTY_SECTIONS) : EMPTY_SECTIONS;
  const shouldDefaultToFirstSection = !requiresSectionSelection && sections.length > 0 && !allSectionsSelected;
  const sectionId = sections.some((section) => section.id === preferredSectionId)
    ? preferredSectionId
    : shouldDefaultToFirstSection
      ? (sections[0]?.id ?? null)
      : null;

  const setSectionId = useCallback(
    (nextSectionId: string | null) => {
      setAllSectionsSelected(nextSectionId === null);
      setSearchParams(
        (currentParams) => {
          const nextParams = new URLSearchParams(currentParams);

          if (nextSectionId) {
            nextParams.set("sectionId", nextSectionId);
          } else {
            nextParams.delete("sectionId");
          }

          return nextParams;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  useEffect(() => {
    if (!queryEnabled || loading || error || data === undefined || preferredSectionId === sectionId) {
      return;
    }

    setSectionId(sectionId);
  }, [data, error, loading, preferredSectionId, queryEnabled, sectionId, setSectionId]);

  const retry = useCallback(() => {
    void refetch();
  }, [refetch]);

  const selection = useMemo<CareerServicesDashboardSectionSelection>(
    () => ({
      sections,
      sectionId,
      setSectionId,
      requiresSectionSelection,
      loaded: capabilitiesLoaded,
      canView: canViewCareerServicesDashboard,
      loading,
      error,
      retry,
      ready:
        !queryEnabled ||
        (!loading &&
          !error &&
          (!requiresSectionSelection || sectionId !== null) &&
          (!preferredSectionId || (data !== undefined && sectionId !== null)))
    }),
    [
      canViewCareerServicesDashboard,
      capabilitiesLoaded,
      data,
      error,
      loading,
      preferredSectionId,
      queryEnabled,
      requiresSectionSelection,
      retry,
      sectionId,
      sections,
      setSectionId
    ]
  );

  return (
    <CareerServicesDashboardSectionSelectionContext.Provider value={selection}>
      {children}
    </CareerServicesDashboardSectionSelectionContext.Provider>
  );
}
